import { ReactNode, useEffect, useRef, useState } from "react";
import { Portal, Overlay, useFocusTrap } from "@growtherapy/sprout-ui";
import classNames from "classnames";

const DIALOG_Z_INDEX = 100;

interface CustomModalProps {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  onClose?: () => void;
  onOutsideClick?: () => void;
}

/**
 * Simplified Modal component based on Sprout's Modal but without animations or
 * onAnimationEnd handler and with immediate unmount when isOpen=false.
 */
export function CustomModal({
  children,
  className,
  isOpen,
  onClose,
  onOutsideClick,
}: CustomModalProps): JSX.Element | null {
  const [prevActiveElement, setPrevActiveElement] = useState<HTMLElement>();

  // Store and restore focus
  useEffect(() => {
    if (isOpen) {
      setPrevActiveElement(document.activeElement as HTMLElement);
    } else if (prevActiveElement) {
      prevActiveElement.focus();
    }
  }, [isOpen, prevActiveElement]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Portal name="modal" zIndex={DIALOG_Z_INDEX}>
      <Overlay
        onClick={(e): void => {
          e.stopPropagation();
          onOutsideClick?.();
        }}
        overlayType="modal"
      >
        <CustomModalBody className={className}>{children}</CustomModalBody>
      </Overlay>
    </Portal>
  );
}

function CustomModalBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap({ elementRef: dialogRef, children });

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      className={classNames(
        className,
        "fixed bottom-0 flex max-h-full w-screen flex-col bg-neutral-300 sm:max-h-[80vh] outline outline-1 outline-neutral-800 sm:relative sm:mx-auto sm:rounded-lg",
      )}
    >
      {/* Using a second nested div to apply the onClick handler allows us to prevent
          event propagation to the parent while preserving the semantic role of the
          parent and communicating to assistive technologies that this child is not
          meant to be human-interactive. */}
      <div
        role="presentation"
        // Prevent event propagation to the parent (e.g. prevent triggering onOutsideClick)
        onClick={(e): void => {
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}
